/**
 * IAAttestationCard Component
 * Collapsible card for each selected IA activity with attestation form
 */

import { useState, useCallback, useRef, useEffect } from 'react';
import { useProgramYear } from '@/contexts/ProgramYearContext';
import { Card, CardContent, CardHeader } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Checkbox } from '@/components/ui/checkbox';
import { Separator } from '@/components/ui/separator';
import { Progress } from '@/components/ui/progress';
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from '@/components/ui/collapsible';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from '@/components/ui/alert-dialog';
import { 
  ChevronDown, 
  ChevronRight, 
  CheckCircle2, 
  Circle, 
  AlertCircle,
  Calendar,
  Users,
  FileText,
  Trash2,
  RefreshCw,
  AlertTriangle
} from 'lucide-react';
import { DateRangePicker, toLocalDateString } from '@/components/ui/date-range-picker';
import { 
  type IAAttestation, 
  type IAAttestationInput,
  type IAAttestationDocument,
  getStatusLabel,
  getStatusBadgeVariant,
  calculateDaysBetween,
  calculateParticipationPercentage,
  validatePerformancePeriod,
  validateClinicianParticipation,
  uploadIADocument,
  deleteIADocument,
  downloadIADocument,
  getIADocuments,
} from '@/lib/ia-calculator-utils';
import { 
  formatActivityId, 
  getSubcategoryIdFromActivityId, 
  getSubcategoryName 
} from '@/lib/ia-data-utils';
import { type Provider } from '@/lib/measure-outcomes';
import { IADocumentUpload } from './IADocumentUpload';

interface IAAttestationCardProps {
  attestation: IAAttestation;
  practiceId: string;
  isExpanded: boolean;
  onToggleExpand: () => void;
  onChange: (updates: IAAttestationInput) => void;
  onRemove: () => void;
  isGroupLevel: boolean;
  providers: Provider[];
  disabled?: boolean;
  isSaving?: boolean;
}

export function IAAttestationCard({
  attestation,
  practiceId,
  isExpanded,
  onToggleExpand,
  onChange,
  onRemove,
  isGroupLevel,
  providers,
  disabled = false,
  isSaving = false,
}: IAAttestationCardProps) {
  const { programYear } = useProgramYear();
  // Local state for form fields
  const [notes, setNotes] = useState(attestation.documentation_notes || '');
  const [attestedByName, setAttestedByName] = useState(attestation.attested_by_name || '');
  const [documents, setDocuments] = useState<IAAttestationDocument[]>(attestation.documents || []);
  const [isLoadingDocs, setIsLoadingDocs] = useState(false);
  
  // Debounce ref for notes auto-save
  const notesDebounceRef = useRef<NodeJS.Timeout | null>(null);
  
  // Load documents when expanded and attestation has an ID
  useEffect(() => {
    if (isExpanded && attestation.id && documents.length === 0 && !isLoadingDocs) {
      loadDocuments();
    }
  }, [isExpanded, attestation.id]);
  
  const loadDocuments = useCallback(async () => {
    if (!attestation.id) return;
    setIsLoadingDocs(true);
    try {
      // Scope by the attestation's own NPI (null = group) and the viewing year
      const docs = await getIADocuments(practiceId, attestation.ia_activity_id, {
        npi: attestation.npi || undefined,
        year: programYear,
      });
      setDocuments(docs);
    } catch (error) {
      console.error('Error loading documents:', error);
    } finally {
      setIsLoadingDocs(false);
    }
  }, [practiceId, attestation.ia_activity_id, attestation.id, attestation.npi, programYear]);
  
  // Handle document upload
  const handleDocumentUpload = useCallback(async (file: File, category: string) => {
    if (!attestation.id) {
      throw new Error('Please save the attestation first before uploading documents');
    }
    const newDoc = await uploadIADocument(practiceId, attestation.id, file, category);
    setDocuments(prev => [...prev, newDoc]);
    // Trigger a re-fetch to update attestation counts
    onChange({ documentation_notes: attestation.documentation_notes });
  }, [practiceId, attestation.id, attestation.documentation_notes, onChange]);
  
  // Handle document delete
  const handleDocumentDelete = useCallback(async (docId: number) => {
    const success = await deleteIADocument(practiceId, docId);
    if (success) {
      setDocuments(prev => prev.filter(d => d.id !== docId));
      // Trigger a re-fetch to update attestation counts
      onChange({ documentation_notes: attestation.documentation_notes });
    }
  }, [practiceId, attestation.documentation_notes, onChange]);
  
  // Handle document download
  const handleDocumentDownload = useCallback(async (doc: IAAttestationDocument) => {
    await downloadIADocument(practiceId, doc);
  }, [practiceId]);

  // Derived values
  const activityId = attestation.ia_activity_id;
  const subcatId = getSubcategoryIdFromActivityId(activityId);
  const activity = attestation.activity;
  const dayCount = calculateDaysBetween(attestation.performance_start, attestation.performance_end);
  const participationPct = calculateParticipationPercentage(
    attestation.clinician_participation_count,
    attestation.clinician_total_count
  );

  // Validation (against the viewing year, not the 2025 default)
  const periodErrors = validatePerformancePeriod(
    attestation.performance_start,
    attestation.performance_end,
    programYear
  );
  const participationError = validateClinicianParticipation(
    attestation.clinician_participation_count,
    attestation.clinician_total_count,
    isGroupLevel
  );
  const hasValidationErrors = periodErrors.length > 0 || !!participationError;

  // Status icon
  const StatusIcon = attestation.is_valid 
    ? CheckCircle2 
    : attestation.status === 'in_progress' 
      ? AlertCircle 
      : Circle;
  const statusIconColor = attestation.is_valid 
    ? 'text-emerald-500' 
    : attestation.status === 'in_progress' 
      ? 'text-amber-500' 
      : 'text-gray-300';

  // Handle date changes
  const handleStartDateChange = useCallback((date: Date | undefined) => {
    const dateStr = date ? toLocalDateString(date) : null;
    onChange({ 
      performance_start: dateStr,
      status: attestation.status === 'not_started' ? 'in_progress' : attestation.status
    });
  }, [onChange, attestation.status]);

  const handleEndDateChange = useCallback((date: Date | undefined) => {
    const dateStr = date ? toLocalDateString(date) : null;
    onChange({ 
      performance_end: dateStr,
      status: attestation.status === 'not_started' ? 'in_progress' : attestation.status
    });
  }, [onChange, attestation.status]);

  // Handle notes change with debounce
  const handleNotesChange = useCallback((value: string) => {
    setNotes(value);
    
    if (notesDebounceRef.current) {
      clearTimeout(notesDebounceRef.current);
    }
    
    notesDebounceRef.current = setTimeout(() => {
      onChange({ 
        documentation_notes: value,
        status: attestation.status === 'not_started' ? 'in_progress' : attestation.status
      });
    }, 1000);
  }, [onChange, attestation.status]);

  // Cleanup debounce on unmount
  useEffect(() => {
    return () => {
      if (notesDebounceRef.current) {
        clearTimeout(notesDebounceRef.current);
      }
    };
  }, []);

  // Handle attestation checkbox
  const handleAttestationChange = useCallback((checked: boolean) => {
    onChange({
      is_attested: checked,
      attested_by_name: checked ? attestedByName : null,
      status: checked ? 'completed' : 'in_progress'
    });
  }, [onChange, attestedByName]);

  // Handle clinician selection toggle
  const handleClinicianToggle = useCallback((npi: string, selected: boolean) => {
    const currentNpis = attestation.participating_npis || [];
    const newNpis = selected 
      ? [...currentNpis, npi]
      : currentNpis.filter(n => n !== npi);
    
    onChange({
      participating_npis: newNpis,
      clinician_participation_count: newNpis.length,
      clinician_total_count: providers.length,
      status: attestation.status === 'not_started' ? 'in_progress' : attestation.status
    });
  }, [onChange, attestation.participating_npis, attestation.status, providers.length]);

  // Select/deselect all clinicians
  const handleSelectAllClinicians = useCallback(() => {
    const allNpis = providers.map(p => p.npi);
    onChange({
      participating_npis: allNpis,
      clinician_participation_count: allNpis.length,
      clinician_total_count: providers.length,
      status: attestation.status === 'not_started' ? 'in_progress' : attestation.status
    });
  }, [onChange, providers, attestation.status]);

  const handleDeselectAllClinicians = useCallback(() => {
    onChange({
      participating_npis: [],
      clinician_participation_count: 0,
      clinician_total_count: providers.length,
      status: attestation.status === 'not_started' ? 'in_progress' : attestation.status
    });
  }, [onChange, providers.length, attestation.status]);

  return (
    <Card className={`
      transition-all
      ${attestation.is_valid ? 'border-l-4 border-l-emerald-500' : ''}
      ${attestation.status === 'in_progress' && !attestation.is_valid ? 'border-l-4 border-l-amber-500' : ''}
    `}>
      <Collapsible open={isExpanded} onOpenChange={onToggleExpand}>
        {/* Card Header - Always Visible */}
        <CollapsibleTrigger asChild>
          <CardHeader className="cursor-pointer hover:bg-slate-50/50 transition-colors py-5">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                {/* Expand/Collapse Icon */}
                {isExpanded ? (
                  <ChevronDown className="w-5 h-5 text-gray-400" />
                ) : (
                  <ChevronRight className="w-5 h-5 text-gray-400" />
                )}
                
                {/* Status Icon */}
                <StatusIcon className={`w-5 h-5 ${statusIconColor}`} />
                
                {/* Activity Info */}
                <div>
                  <div className="flex items-center gap-2">
                    <span className="font-semibold text-gray-900">
                      {activityId}
                    </span>
                    {subcatId && (
                      <span className={`text-xs font-medium uppercase tracking-wide whitespace-nowrap ${
                        subcatId === 'AHE' ? 'text-purple-600' :
                        subcatId === 'BE' ? 'text-blue-600' :
                        subcatId === 'CC' ? 'text-teal-600' :
                        subcatId === 'EPA' ? 'text-green-600' :
                        subcatId === 'PM' ? 'text-orange-600' :
                        subcatId === 'PSPA' ? 'text-red-600' :
                        subcatId === 'BMH' ? 'text-indigo-600' :
                        subcatId === 'ERP' ? 'text-rose-600' :
                        'text-gray-600'
                      }`}>
                        {getSubcategoryName(subcatId)}
                      </span>
                    )}
                  </div>
                  <p className="text-sm text-gray-600 line-clamp-1">
                    {activity?.title || activityId}
                  </p>
                </div>
              </div>

              {/* Right Side - Status */}
              <div className="flex items-center gap-4">
                {/* Status Badge */}
                <Badge variant={getStatusBadgeVariant(attestation.status)}>
                  {getStatusLabel(attestation.status)}
                </Badge>

                {/* Saving Indicator */}
                {isSaving && (
                  <RefreshCw className="w-4 h-4 animate-spin text-gray-400" />
                )}
              </div>
            </div>
          </CardHeader>
        </CollapsibleTrigger>

        {/* Expandable Content */}
        <CollapsibleContent>
          <Separator />
          <CardContent className="pt-6 space-y-6">
            {/* Activity Info Section */}
            <div className="space-y-2">
              <h4 className="text-sm font-medium text-gray-700">Activity Description</h4>
              <p className="text-sm text-gray-600 bg-slate-50 p-3 rounded-lg">
                {activity?.description || 'No description available'}
              </p>
            </div>

            {/* Performance Period Section */}
            <div className="space-y-3">
              <div className="flex items-center gap-2">
                <Calendar className="w-4 h-4 text-gray-500" />
                <h4 className="text-sm font-medium text-gray-700">Performance Period</h4>
              </div>
              
              <DateRangePicker
                startDate={attestation.performance_start}
                endDate={attestation.performance_end}
                onStartDateChange={handleStartDateChange}
                onEndDateChange={handleEndDateChange}
                minDate={new Date(programYear, 0, 1)}
                maxEndDate={new Date(programYear, 11, 31)}
                disabled={disabled}
              />

              {/* Duration Display */}
              {dayCount !== null && (
                <div className={`
                  flex items-center gap-2 text-sm p-2 rounded
                  ${dayCount >= 90 ? 'bg-emerald-50 text-emerald-700' : 'bg-amber-50 text-amber-700'}
                `}>
                  {dayCount >= 90 ? (
                    <CheckCircle2 className="w-4 h-4" />
                  ) : (
                    <AlertTriangle className="w-4 h-4" />
                  )}
                  <span>
                    Duration: {dayCount} days 
                    {dayCount < 90 && ' (Minimum 90 days required)'}
                  </span>
                </div>
              )}
            </div>

            {/* Clinician Participation Section (Group Only) */}
            {isGroupLevel && providers.length > 0 && (
              <div className="space-y-3">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    <Users className="w-4 h-4 text-gray-500" />
                    <h4 className="text-sm font-medium text-gray-700">Participating Clinicians</h4>
                  </div>
                  <span className={`text-sm font-medium ${participationPct > 50 ? 'text-emerald-600' : 'text-amber-600'}`}>
                    {attestation.clinician_participation_count} of {providers.length} ({participationPct}%)
                  </span>
                </div>

                {/* Progress Bar */}
                <div className="space-y-1">
                  <Progress 
                    value={participationPct} 
                    className={`h-2 ${participationPct > 50 ? '[&>div]:bg-emerald-500' : '[&>div]:bg-amber-500'}`}
                  />
                  <p className="text-xs text-gray-500">
                    {participationPct > 50 ? '✓ Meets 50% requirement' : '⚠️ Must exceed 50%'}
                  </p>
                </div>

                {/* Quick Actions */}
                <div className="flex gap-2">
                  <Button variant="outline" size="sm" onClick={handleSelectAllClinicians} disabled={disabled}>
                    Select All
                  </Button>
                  <Button variant="outline" size="sm" onClick={handleDeselectAllClinicians} disabled={disabled}>
                    Deselect All
                  </Button>
                </div>

                {/* Clinician List */}
                <div className="max-h-48 overflow-y-auto border rounded-lg">
                  {providers.map((provider) => {
                    const isSelected = (attestation.participating_npis || []).includes(provider.npi);
                    return (
                      <div
                        key={provider.npi}
                        className={`
                          flex items-center gap-3 p-2 border-b last:border-b-0 cursor-pointer hover:bg-slate-50
                          ${isSelected ? 'bg-emerald-50' : ''}
                        `}
                        onClick={() => !disabled && handleClinicianToggle(provider.npi, !isSelected)}
                      >
                        <Checkbox 
                          checked={isSelected} 
                          disabled={disabled}
                          onCheckedChange={(checked) => handleClinicianToggle(provider.npi, !!checked)}
                        />
                        <div>
                          <div className="text-sm font-medium">{provider.displayName}</div>
                          <div className="text-xs text-gray-500">{provider.npi}</div>
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>
            )}

            {/* Documentation Notes Section */}
            <div className="space-y-3">
              <div className="flex items-center gap-2">
                <FileText className="w-4 h-4 text-gray-500" />
                <h4 className="text-sm font-medium text-gray-700">Documentation Notes</h4>
              </div>
              
              <Textarea
                placeholder="Describe how your practice implemented this activity..."
                value={notes}
                onChange={(e) => handleNotesChange(e.target.value)}
                disabled={disabled}
                rows={4}
                className="resize-none"
              />
              <div className="flex justify-between text-xs text-gray-500">
                <span>{notes.length} / 2000 characters</span>
                <span>Auto-saves as you type</span>
              </div>
            </div>

            {/* Document Upload Section */}
            <div className="space-y-3">
              <IADocumentUpload
                practiceId={parseInt(practiceId)}
                attestationId={attestation.id}
                documents={documents}
                onUpload={handleDocumentUpload}
                onDelete={handleDocumentDelete}
                onDownload={handleDocumentDownload}
                disabled={disabled}
              />
            </div>

            {/* Attestation Section */}
            <div className="space-y-4 p-4 bg-slate-50 rounded-lg border">
              <div className="flex items-start gap-3">
                <Checkbox
                  id={`attest-${activityId}`}
                  checked={attestation.is_attested}
                  onCheckedChange={handleAttestationChange}
                  disabled={disabled || hasValidationErrors}
                />
                <div className="space-y-1">
                  <Label htmlFor={`attest-${activityId}`} className="text-sm leading-relaxed cursor-pointer">
                    I attest that this improvement activity was performed for at least 90 continuous days 
                    during the {programYear} performance year
                    {isGroupLevel && ', and that more than 50% of clinicians in this TIN completed this activity'}.
                  </Label>
                </div>
              </div>

              {attestation.is_attested && (
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 pt-2">
                  <div className="space-y-2">
                    <Label htmlFor={`attested-by-${activityId}`}>Attested by</Label>
                    <Input
                      id={`attested-by-${activityId}`}
                      value={attestedByName}
                      onChange={(e) => setAttestedByName(e.target.value)}
                      onBlur={() => onChange({ attested_by_name: attestedByName })}
                      placeholder="Your name"
                      disabled={disabled}
                    />
                  </div>
                  <div className="space-y-2">
                    <Label>Date</Label>
                    <Input
                      value={attestation.attested_at ? new Date(attestation.attested_at).toLocaleDateString() : 'Auto-filled'}
                      disabled
                      className="bg-gray-100"
                    />
                  </div>
                </div>
              )}

              {/* Validation Errors */}
              {hasValidationErrors && (
                <div className="mt-3 p-3 bg-red-50 border border-red-200 rounded-lg">
                  <p className="text-sm font-medium text-red-800 mb-1">Please resolve the following:</p>
                  <ul className="text-sm text-red-700 list-disc list-inside">
                    {periodErrors.map((err, i) => (
                      <li key={i}>{err.message}</li>
                    ))}
                    {participationError && <li>{participationError.message}</li>}
                  </ul>
                </div>
              )}
            </div>

            {/* Remove Activity */}
            <div className="flex justify-end pt-2">
              <AlertDialog>
                <AlertDialogTrigger asChild>
                  <Button variant="ghost" size="sm" disabled={disabled} className="text-red-600 hover:text-red-700 hover:bg-red-50">
                    <Trash2 className="w-4 h-4 mr-2" />
                    Remove Activity
                  </Button>
                </AlertDialogTrigger>
                <AlertDialogContent>
                  <AlertDialogHeader>
                    <AlertDialogTitle>Remove Activity?</AlertDialogTitle>
                    <AlertDialogDescription>
                      This will remove {formatActivityId(activityId)} from your selected activities. 
                      Any attestation data entered will be lost.
                    </AlertDialogDescription>
                  </AlertDialogHeader>
                  <AlertDialogFooter>
                    <AlertDialogCancel>Cancel</AlertDialogCancel>
                    <AlertDialogAction onClick={onRemove} className="bg-red-600 hover:bg-red-700">
                      Remove
                    </AlertDialogAction>
                  </AlertDialogFooter>
                </AlertDialogContent>
              </AlertDialog>
            </div>
          </CardContent>
        </CollapsibleContent>
      </Collapsible>
    </Card>
  );
}

